"use client";
import { usePathname, useSearchParams } from "next/navigation";
import NProgress from "nprogress";
import { useEffect, useRef } from "react";

// Configure NProgress
NProgress.configure({ showSpinner: false });

export function Progress() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isNavigatingRef = useRef(false);

  // Track route changes and stop progress when navigation completes
  useEffect(() => {
    if (isNavigatingRef.current) {
      NProgress.done();
      isNavigatingRef.current = false;
    }
  }, [pathname, searchParams]);

  // Intercept link clicks to start progress bar
  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const anchor = target.closest("a");

      if (!anchor) return;

      const href = anchor.getAttribute("href");
      if (!href) return;

      // Skip external links, hash links, and special protocols
      if (
        href.startsWith("http") ||
        href.startsWith("//") ||
        href.startsWith("#") ||
        href.startsWith("mailto:") ||
        href.startsWith("tel:") ||
        anchor.getAttribute("target") === "_blank" ||
        anchor.getAttribute("download") !== null
      ) {
        return;
      }

      // Skip if modifier keys are pressed (opens in new tab)
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }

      // Check if navigating to a different page
      const currentUrl = `${window.location.pathname}${window.location.search}`;
      const newUrl = href.split("#")[0]; // Remove hash for comparison

      if (newUrl && newUrl !== currentUrl) {
        isNavigatingRef.current = true;
        NProgress.start();
      }
    };

    // Handle browser back/forward buttons
    const handlePopState = () => {
      isNavigatingRef.current = true;
      NProgress.start();
    };

    document.addEventListener("click", handleClick);
    window.addEventListener("popstate", handlePopState);

    return () => {
      document.removeEventListener("click", handleClick);
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  return null;
}
